import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/cn';
import type { Card, Row } from '@/lib/qcp/protocol';
import { ordinaManoCome } from '@/lib/tavolo/ordinaMano';
import type { Ordine } from '@/lib/tavolo/ordinaMano';
import { FRAZIONE_VISIBILE, disponiVentaglio, margineVentaglio } from '@/lib/tavolo/ventaglio';
import { PlayingCard } from './PlayingCard';
import { CHIAVI_ORDINE_CP, MISURE_CP } from './cartaCp';
import type { TagliaCp } from './cartaCp';
import { filaSotto, haTrascinato, segnaRilascioColDito } from './trascinaCp';
import type { TrascinaMano } from './trascinaCp';

interface DrawTrayProps {
  /** The cards currently in the player's draw hand for this round. L'ordine e quello del chiamante: il tavolo li dispone nell'ordine di `ordine`, senza mutare l'array. */
  cards: Card[];
  /** L'ordine in cui disporre la mano (il tasto «Ordina»): per seme o per valore. Senza, per seme (come il Burraco). */
  ordine?: Ordine;
  /**
   * The selected CARD (null = none). ⛔ Una CARTA, mai un indice: con «Ordina» l'ordine che si vede non e quello dell'array, e un indice visivo selezionerebbe la carta sbagliata
   * (v. `ChinesePokerMatch`, che dalla carta ricava l'indice del suo `draw`).
   */
  selectedCard?: Card | null;
  /** Called when the player taps a draw card — con la CARTA toccata, non la sua posizione. */
  onSelect?: (card: Card) => void;
  /**
   * True when the only remaining card is this round's auto-discard (Pineapple:
   * 2 of 3 already placed). The tray then labels it as the discard and the card
   * is shown non-selectable with a red ring — instead of "Your draw".
   */
  discarding?: boolean;
  /** La taglia delle carte in mano: `mano` (in piedi) o `mano-palco`. Senza, `mano`. Oltre 5 carte (la Fantasyland) scendono a `avv`. */
  taglia?: TagliaCp;
  quattroColori?: boolean;
  /** L'etichetta «Le tue carte»: nel palco si, in piedi no (budget di altezza); «Scarti questa carta» si mostra sempre. */
  conEtichetta?: boolean;
  /** Nel palco, la mano LARGA (la Fantasyland in una striscia sotto il tavolo, non nella colonna da 184 px): serve solo a dire la larghezza di ripiego senza schermo. */
  larga?: boolean;
  /**
   * Il TRASCINAMENTO (I-798, fase B): con questo, una carta si prende col dito e si lascia su una fila (`onRilascio`); il TOCCO resta com'e (`onSelect`). Senza, la mano non si trascina.
   * Una carta scartata (`discarding`) non si trascina: non c'e piu niente da scegliere.
   */
  trascina?: TrascinaMano;
  /**
   * La mano e quella della FANTASYLAND (14-17 carte, poi le 1-4 che restano da scartare): le carte hanno SEMPRE la taglia `avv`. Senza, quando restano 1-4 carte la mano tornerebbe alla taglia
   * normale e crescerebbe di 20 px proprio quando il tavolo in piedi non ne ha da dare (624/604 a 360×780, misurato dal banco).
   */
  fantasyland?: boolean;
  /**
   * Il ventaglio SENZA arco (le carte ai bordi non scendono rispetto a quelle di mezzo): chi taglia la mano in basso (il tutorial, sotto i 700 px) ci guadagna la parte visibile delle carte ai bordi, cosi' rango e seme
   * restano interi (qchinesepoker-orcb-14, tma-ui-orcb 28/10). Senza, il ventaglio di sempre: il tavolo vivo non lo passa.
   */
  senzaArco?: boolean;
}

/** La carta che si sta trascinando: dove e il dito e la misura che la carta ha sullo SCHERMO (la mano puo essere scalata dal palco). */
interface Volo {
  card: Card;
  x: number;
  y: number;
  /** Larghezza e altezza della carta come si vede sullo schermo. */
  w: number;
  h: number;
  /** Col dito (touch) la carta sta sopra il dito, perche non lo copra. */
  sopra: boolean;
}

/** Le carte oltre questa soglia sono la Fantasyland (14-17): scendono di taglia per stare su UNA fila. */
const SOGLIA_FANTASYLAND = 5;
/**
 * La larghezza che la mano ha PRIMA di essere misurata (la prima pittura) o quando il browser non misura (jsdom). Nel palco e' fissa (il palco e' un disegno scalato: colonna di destra o
 * striscia sotto il tavolo). In piedi e' quella dello schermo meno i due padding (rotta 2×12 px, panno 2×8 px, con il tetto di 520 px della rotta): una stima esatta sul telefono
 * (360 → 320, 390 → 350). ⚠️ Serve a non far muovere le carte fra la prima pittura e la misura: lo spostamento lo prenderebbe per un volo `useVoloCarte` (FLIP).
 */
const RIPIEGO_COLONNA_PALCO = 184;
const RIPIEGO_STRISCIA_PALCO = 616;
const PADDING_PIEDI = 40;
const RIPIEGO_PIEDI_SENZA_SCHERMO = 340;
const ripiegoPiedi = (): number => (typeof window !== 'undefined' && window.innerWidth > 0 ? Math.max(Math.min(window.innerWidth, 520) - PADDING_PIEDI, 120) : RIPIEGO_PIEDI_SENZA_SCHERMO);

/**
 * La larghezza (px) del contenitore della mano. Come `useLarghezza` (copia da TMA#718) ma: (1) misura in `useLayoutEffect`, cosi' la PRIMA pittura ha gia' la larghezza vera e le carte non
 * scivolano dal ripiego alla misura (misurato dal banco: 1 px di sbordo a 250 ms); (2) tiene l'elemento in uno STATO, cosi' una mano che nasce vuota e si riempie dopo (il giro nuovo) viene misurata
 * quando l'elemento compare, non mai. `ripiego` se il browser non misura (jsdom).
 */
function useLarghezzaMano(ripiego: number) {
  const [el, setEl] = useState<HTMLDivElement | null>(null);
  const [larghezza, setLarghezza] = useState(ripiego);
  useLayoutEffect(() => {
    if (!el) return;
    const misura = () => {
      const w = el.clientWidth;
      if (w > 0) setLarghezza(w);
    };
    misura();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(misura);
    ro.observe(el);
    return () => ro.disconnect();
  }, [el]);
  return { ref: setEl, larghezza };
}

/**
 * The player's draw tray — the set of cards dealt for this round, a VENTAGLIO as the Burraco's hand (`lib/tavolo/ventaglio.ts`, copia da TMA#718): le carte si SOVRAPPONGONO
 * su UNA fila con un passo calcolato dalla larghezza (14-17 carte della Fantasyland comprese), si aprono e crescono con poche carte, e vanno su due file solo sotto il passo
 * minimo. Each card is tappable; the selected card is lifted and gets the brand ring. The label is the gold small-caps «LE TUE CARTE» of the tables.
 *
 * In round 0 all 5 cards are shown (initial deal).
 * In rounds 1-4 the Pineapple draw-3 gives 3 cards; the tray renders exactly
 * what `cards` contains (the caller passes only the current draw).
 *
 * L'altezza della mano e quella della carta di base (50×70 in piedi con la mano d'apertura): le carte che crescono si tagliano in basso, il tavolo in piedi non ha un pixel da cedere.
 */
export function DrawTray({ cards, ordine = 'seme', selectedCard = null, onSelect, discarding = false, taglia = 'mano', quattroColori = false, conEtichetta = false, larga = false, trascina, fantasyland = false, senzaArco = false }: DrawTrayProps) {
  const { t } = useTranslation('chinesepoker');
  const palco = taglia === 'mano-palco';
  const { ref, larghezza } = useLarghezzaMano(palco ? (larga ? RIPIEGO_STRISCIA_PALCO : RIPIEGO_COLONNA_PALCO) : ripiegoPiedi());
  // La transizione si accende DOPO la prima misura: la mano nasce con la larghezza di ripiego e, misurata, si risistema; senza questo le carte scivolerebbero dal ripiego alla misura.
  const [animata, setAnimata] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setAnimata(true));
    return () => cancelAnimationFrame(id);
  }, []);
  // ── Il trascinamento ──────────────────────────────────────────────────────────────────────────────────────────────────────────────────────────
  // Il TOCCO resta il click del bottone della carta. Il trascinamento comincia quando il dito si e mosso piu della soglia: la carta e «presa» (`onInizio`), una copia segue il dito, e al
  // rilascio la rotta decide (`onRilascio`: su una fila legale la piazza dallo STESSO percorso del tocco, fuori la rimette com'era). La mano non conosce regole: sa solo dove e il dito.
  const trascinaRef = useRef(trascina);
  trascinaRef.current = trascina;
  const [volo, setVolo] = useState<Volo | null>(null);
  const presa = useRef<{ id: number; card: Card; x0: number; y0: number; trascina: boolean; fila: Row | null; el: HTMLElement; chiudi: () => void } | null>(null);
  // Dopo un trascinamento il browser manda comunque un `click` al bottone della carta: va scartato, o la carta lasciata verrebbe anche «toccata».
  const scartaClick = useRef(false);
  // quanto il ventaglio ingrandisce le carte adesso (la copia che segue il dito non lo segue: v. `muovi`)
  const scalaVentaglio = useRef(1);
  useEffect(() => () => presa.current?.chiudi(), []);

  const prendi = (e: ReactPointerEvent<HTMLDivElement>, card: Card) => {
    if (!trascinaRef.current || presa.current || (e.pointerType === 'mouse' && e.button !== 0) || !e.isPrimary) return;
    const el = e.currentTarget;
    const corrente = { id: e.pointerId, card, x0: e.clientX, y0: e.clientY, trascina: false, fila: null as Row | null, el, chiudi: () => {} };
    const fine = () => {
      window.removeEventListener('pointermove', muovi);
      window.removeEventListener('pointerup', alza);
      window.removeEventListener('pointercancel', annulla);
      window.removeEventListener('keydown', tasto);
      presa.current = null;
      setVolo(null);
    };
    const muovi = (ev: PointerEvent) => {
      if (ev.pointerId !== corrente.id) return;
      if (!corrente.trascina) {
        if (!haTrascinato(ev.clientX - corrente.x0, ev.clientY - corrente.y0)) return;
        corrente.trascina = true;
        trascinaRef.current?.onInizio(card);
      }
      // la misura SULLO SCHERMO: la mano nel palco e scalata (e girata di 90° col telefono in piedi), la copia no — la scala si ricava dalle aree
      const r = corrente.el.getBoundingClientRect();
      const area = Math.sqrt((r.width * r.height) / Math.max(corrente.el.offsetWidth * corrente.el.offsetHeight, 1));
      // la copia non e grande quanto la carta della mano quando questa e ingrandita dal ventaglio (poche carte): non deve coprire la fila su cui si lascia — al piu 1,2 volte la carta base
      const riduci = Math.min(1, 1.2 / Math.max(scalaVentaglio.current, 1));
      const w = corrente.el.offsetWidth * area * riduci;
      const h = corrente.el.offsetHeight * area * riduci;
      setVolo({ card, x: ev.clientX, y: ev.clientY, w, h, sopra: ev.pointerType === 'touch' });
      const fila = filaSotto(ev.clientX, ev.clientY);
      if (fila !== corrente.fila) {
        corrente.fila = fila;
        trascinaRef.current?.onSopra(fila);
      }
    };
    const alza = (ev: PointerEvent) => {
      if (ev.pointerId !== corrente.id) return;
      const eraTrascinata = corrente.trascina;
      const fila = eraTrascinata ? filaSotto(ev.clientX, ev.clientY) : null;
      fine();
      if (!eraTrascinata) return;
      scartaClick.current = true;
      window.setTimeout(() => (scartaClick.current = false), 0);
      trascinaRef.current?.onSopra(null);
      // la carta lasciata su una fila e arrivata col dito: nella fila non deve rivolare dalla mano (I-807)
      if (fila) segnaRilascioColDito(`${card.r}${card.s}`);
      trascinaRef.current?.onRilascio(card, fila);
    };
    const annulla = (ev?: Event) => {
      if (ev && 'pointerId' in ev && (ev as PointerEvent).pointerId !== corrente.id) return;
      const eraTrascinata = corrente.trascina;
      fine();
      if (!eraTrascinata) return;
      trascinaRef.current?.onSopra(null);
      trascinaRef.current?.onRilascio(card, null);
    };
    const tasto = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') annulla();
    };
    corrente.chiudi = fine;
    presa.current = corrente;
    window.addEventListener('pointermove', muovi);
    window.addEventListener('pointerup', alza);
    window.addEventListener('pointercancel', annulla);
    window.addEventListener('keydown', tasto);
  };

  if (cards.length === 0) return null;
  const base: TagliaCp = cards.length > SOGLIA_FANTASYLAND || fantasyland ? 'avv' : taglia;
  const { w: cw0, h: ch0 } = MISURE_CP[base];
  const disposte = ordinaManoCome(cards, ordine, CHIAVI_ORDINE_CP);
  const trascinabile = !discarding && Boolean(onSelect) && Boolean(trascina);
  // Lo SCARTO (I-798, fase B): quando resta solo da scartare, la mano dice cosa succede e cosa fare ACCANTO alla carta, non sopra (in piedi non c'e un pixel di altezza da dare: sopra costava
  // 20 px e la pagina scorreva). Nel palco, dove la colonna e stretta, il messaggio sta sopra come prima.
  const accanto = discarding && !palco;
  // il ventaglio dello scarto ha la SUA larghezza (la carta e le sue vicine, sovrapposte): non si misura, si calcola — misurarla era circolare (la larghezza dipendeva dal contenuto)
  const mDiscarding = margineVentaglio(ch0);
  const wDiscarding = cw0 + Math.max(disposte.length - 1, 0) * cw0 * 0.55 + 2 * mDiscarding;
  // la carta dello scarto non cresce (`massima` = la taglia base): ha il suo posto accanto al messaggio, e un ventaglio di una carta sola la ingrandirebbe fino a 75 px
  const v = disponiVentaglio({ n: disposte.length, larghezza: accanto ? wDiscarding : larghezza, cw0, ch0, massima: discarding ? cw0 : cw0 / FRAZIONE_VISIBILE, ...(senzaArco ? { arco: 0 } : {}) });

  scalaVentaglio.current = v.scala;
  const messaggioScarto = (
    <div data-scarto className={cn('min-w-0 leading-tight', accanto ? 'text-left' : 'text-center')}>
      <div className="text-[11px] font-bold uppercase tracking-[.18em] text-danger">✕ {t('match.discardTitle')}</div>
      <div className="text-[13px] font-semibold text-fg">{t('match.discarding', { conferma: t('match.confirm') })}</div>
    </div>
  );

  return (
    <div data-mano-in-mano data-mano-scarto={discarding ? 'true' : undefined} className={cn('flex w-full items-center gap-1', accanto ? 'flex-row justify-center gap-3' : 'flex-col')}>
      {discarding && !accanto && messaggioScarto}
      {conEtichetta && !discarding && <span className="tav-etichetta text-[11px] font-bold uppercase tracking-[.18em]">{t('match.yourDraw')}</span>}
      <div ref={ref} data-mano-ventaglio data-ventaglio-file={v.file} data-ordine={ordine} className={cn('relative [clip-path:inset(-400px_-24px_0_-24px)]', accanto ? 'shrink-0' : 'w-full')} style={{ height: v.altezzaMano, width: accanto ? wDiscarding : undefined, margin: accanto ? `0 -${mDiscarding}px` : undefined }}>
        {disposte.map((card, i) => {
          const inVolo = volo !== null && volo.card.r === card.r && volo.card.s === card.s;
          const scelta = !discarding && !inVolo && selectedCard !== null && selectedCard.r === card.r && selectedCard.s === card.s;
          const { fila, x, y, angolo } = v.posizione(i);
          // il contenitore e grande quanto la carta BASE: lo spostamento compensa la scala, che cresce attorno al centro del bordo basso
          const tx = x + (v.cw - cw0) / 2;
          const ty = y + (v.ch - ch0);
          return (
            <div
              key={`${card.r}${card.s}`}
              data-carta-mano={`${card.r}${card.s}`}
              data-in-volo={inVolo ? 'true' : undefined}
              className={cn('absolute left-0 top-0', animata && 'transition-transform duration-200 ease-out', inVolo && 'opacity-30')}
              onPointerDown={trascinabile ? (e) => prendi(e, card) : undefined}
              onClickCapture={trascinabile ? (e) => { if (scartaClick.current) { e.stopPropagation(); e.preventDefault(); } } : undefined}
              style={{
                // il dito che si muove sulla carta non deve far scorrere la pagina (e, in Telegram, ridurre la mini app): `none` solo sulle carte che si possono trascinare
                touchAction: trascinabile ? 'none' : undefined,
                width: cw0,
                height: ch0,
                transform: `translate(${tx.toFixed(1)}px, ${ty.toFixed(1)}px) rotate(${(scelta ? 0 : angolo).toFixed(2)}deg) scale(${v.scala.toFixed(3)})`,
                transformOrigin: '50% 100%',
                zIndex: fila * 100 + i + 1,
              }}
            >
              <PlayingCard card={card} taglia={base} quattroColori={quattroColori} selected={scelta} discard={discarding} onClick={discarding || !onSelect ? undefined : () => onSelect(card)} />
            </div>
          );
        })}
      </div>
      {accanto && messaggioScarto}
      {volo && typeof document !== 'undefined' && createPortal(<CartaInVolo volo={volo} base={base} quattroColori={quattroColori} cw0={cw0} />, document.body)}
    </div>
  );
}

/** La copia della carta che segue il dito: fuori da tutto (il tavolo ritaglia e scala), alla misura che la carta ha sullo schermo, dritta. */
function CartaInVolo({ volo, base, quattroColori, cw0 }: { volo: Volo; base: TagliaCp; quattroColori: boolean; cw0: number }) {
  const scala = volo.w / cw0;
  return (
    <div
      aria-hidden="true"
      data-carta-trascinata={`${volo.card.r}${volo.card.s}`}
      className="pointer-events-none fixed left-0 top-0 z-[9999]"
      style={{ width: volo.w, height: volo.h, transform: `translate(${volo.x - volo.w / 2}px, ${volo.y - volo.h * (volo.sopra ? 1.15 : 0.5)}px) rotate(-5deg)`, filter: 'drop-shadow(0 8px 10px rgba(0,0,0,.45))' }}
    >
      <div style={{ transform: `scale(${scala})`, transformOrigin: '0 0' }}>
        <PlayingCard card={volo.card} taglia={base} quattroColori={quattroColori} selected />
      </div>
    </div>
  );
}
